import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import {
  Calendar,
  Search,
  Users,
  UserCheck,
  UserX,
  ClipboardList,
  Clock,
  AlertTriangle,
  Filter,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useTimeTracking } from '../../core/contexts/TimeTrackingContext';
import TimeTrackingPeriodToggle from './components/TimeTrackingPeriodToggle';
import { getTimeTrackingCodeLabel } from '../../shared/utils/timeTrackingI18n';
import {
  formatPointageDate,
  formatPointageDateWithHijri,
  isSaudiFixedHoliday,
  isSaudiWeekend,
} from './pointageHelpers';

const DAY_KEY_SUFFIXES = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

function entryMatchesEmployee(entry, emp) {
  if (!entry || !emp) return false;
  const eid = String(entry.employeeId ?? entry.employee_id ?? '');
  const keys = [emp.matricule, emp.employeeId, emp.id]
    .filter((k) => k != null && k !== '')
    .map((k) => String(k));
  return keys.some((k) => k === eid);
}

function normalizeEntryDate(d) {
  if (d == null || d === '') return '';
  const s = String(d);
  return s.length >= 10 ? s.slice(0, 10) : s;
}

function getWeekStartSunday(date = new Date()) {
  const d = new Date(date);
  d.setHours(12, 0, 0, 0);
  d.setDate(d.getDate() - d.getDay());
  return d;
}

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function toDateStr(date) {
  return date.toISOString().slice(0, 10);
}

const WeeklyTimeTrackingPage = () => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.pointage.pages.weeklyTimeTrackingPage.${key}`, opts);
  const pDaily = (key, opts) => t(`modules.pointage.pages.dailyTimeTrackingPage.${key}`, opts);
  const getDayOfWeek = (date) => t(`common.${DAY_KEY_SUFFIXES[date.getDay()]}`);
  const navigate = useNavigate();
  const { getTimeEntriesForMonth, employees, timeTrackingCodes } = useTimeTracking();

  const [weekStart, setWeekStart] = useState(() => getWeekStartSunday(new Date()));
  const [weekEntries, setWeekEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);

  const weekDates = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );

  const weekLabel = useMemo(() => {
    const start = weekDates[0];
    const end = weekDates[6];
    return p('week_range', {
      start: formatPointageDate(start),
      end: formatPointageDate(end),
    });
  }, [weekDates, p]);

  useEffect(() => {
    const fetchWeekData = async () => {
      setIsLoading(true);
      try {
        const monthKeys = new Set(
          weekDates.map((d) => `${d.getFullYear()}-${d.getMonth() + 1}`)
        );
        const fetches = [...monthKeys].map(async (key) => {
          const [year, month] = key.split('-').map(Number);
          return getTimeEntriesForMonth(year, month);
        });
        const results = await Promise.all(fetches);
        const merged = results.flat();
        const weekStrSet = new Set(weekDates.map(toDateStr));
        setWeekEntries(merged.filter((e) => weekStrSet.has(normalizeEntryDate(e.date))));
      } catch (error) {
        console.error('Error fetching weekly data:', error);
        setWeekEntries([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchWeekData();
  }, [weekDates, getTimeEntriesForMonth]);

  const weekData = useMemo(() => {
    const data = {};
    employees.forEach((emp) => {
      if (!emp.isActive) return;
      data[emp.id] = weekDates.map((date) => {
        const dateStr = toDateStr(date);
        const entry = weekEntries.find(
          (e) => entryMatchesEmployee(e, emp) && normalizeEntryDate(e.date) === dateStr
        );
        if (entry) {
          const codeInfo = timeTrackingCodes.find((c) => c.code === entry.status);
          return {
            code: entry.status,
            label: getTimeTrackingCodeLabel(
              entry.status,
              entry.statusLabel || codeInfo?.label || entry.status,
              t
            ),
            color: entry.statusColor || codeInfo?.bgColor || 'bg-gray-500',
            notes: entry.notes || '',
            dateStr,
          };
        }
        if (isSaudiWeekend(date)) {
          return { code: 'W', label: p('label_weekend'), color: 'bg-blue-500', notes: '', dateStr };
        }
        if (isSaudiFixedHoliday(date)) {
          return { code: 'H', label: p('label_holiday'), color: 'bg-amber-500', notes: '', dateStr };
        }
        return { code: '', label: p('label_noData'), color: 'bg-gray-400', notes: '', dateStr };
      });
    });
    return data;
  }, [employees, weekDates, weekEntries, timeTrackingCodes, t, p]);

  const displayedEmployees = useMemo(() => {
    let list = employees.filter((e) => e.isActive);
    const q = searchTerm.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (emp) =>
          emp.name?.toLowerCase().includes(q) ||
          String(emp.id).includes(q) ||
          emp.department?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [employees, searchTerm]);

  const weekStats = useMemo(() => {
    let present = 0;
    let absent = 0;
    let late = 0;
    let totalHours = 0;
    weekEntries.forEach((e) => {
      if (['P', 'P/2'].includes(e.status)) present++;
      else if (['AN', 'AJ'].includes(e.status)) absent++;
      else if (e.status === 'R') late++;
      if (e.totalHours) {
        const [h, m] = String(e.totalHours).split(':').map(Number);
        totalHours += (h || 0) + (m || 0) / 60;
      }
    });
    const h = Math.floor(totalHours);
    const m = Math.round((totalHours - h) * 60);
    return {
      effectif: displayedEmployees.length,
      present,
      absent,
      late,
      totalHours: `${h}:${String(m).padStart(2, '0')}`,
    };
  }, [weekEntries, displayedEmployees.length]);

  const prevWeek = () => setWeekStart((ws) => addDays(ws, -7));
  const nextWeek = () => setWeekStart((ws) => addDays(ws, 7));
  const goToday = () => setWeekStart(getWeekStartSunday(new Date()));

  const handleCellClick = (employee, dayIndex) => {
    const cell = weekData[employee.id]?.[dayIndex];
    if (!cell?.dateStr) return;
    navigate(`/timetracking/daily?date=${cell.dateStr}`);
  };

  if (isLoading) {
    return (
      <div className="clients-container bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 p-6 min-h-[50vh] flex items-center justify-center">
        <p className="text-white text-lg">{p('loading')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 clients-container">
      <div className="flex justify-between items-center list-toolbar-mobile-layout">
        <div>
          <h1 className="text-3xl font-bold text-white">{p('title')}</h1>
          <p className="text-white/60 mt-2">{p('subtitle')}</p>
          <div className="mt-3 inline-flex items-center gap-2 flex-wrap">
            <Calendar size={16} className="text-white/50 shrink-0" />
            <span className="block w-full text-white/70 text-sm font-medium text-right">{p('displayedPeriod')}</span>
            <span className="px-3 py-1.5 rounded-lg text-sm font-semibold border bg-white/10 text-white/90 border-white/30">
              {weekLabel}
            </span>
          </div>
        </div>
        <div className="flex gap-3 flex-wrap justify-end">
          <button
            type="button"
            onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
            className={`${
              showAdvancedOptions
                ? 'bg-gradient-to-r from-purple-500 to-purple-600 border-purple-400'
                : 'bg-white/5 border-white/20'
            } border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}
            title={pDaily('advancedOptions')}
          >
            <Filter className="h-5 w-5 shrink-0" />
            <span className="hidden sm:inline">{pDaily('advancedOptions')}</span>
            {showAdvancedOptions ? <ChevronUp className="h-4 w-4 shrink-0" /> : <ChevronDown className="h-4 w-4 shrink-0" />}
          </button>
          <div className="flex items-center rounded-xl border border-white/20 bg-white/5 overflow-hidden">
            <button
              type="button"
              onClick={goToday}
              className="px-4 py-3 text-white text-sm font-medium hover:bg-white/10 border-r border-white/20"
            >
              {p('this_week')}
            </button>
            <button type="button" onClick={prevWeek} className="p-3 hover:bg-white/10 text-white" aria-label={p('prev_week')}>
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
            <button type="button" onClick={nextWeek} className="p-3 hover:bg-white/10 text-white" aria-label={p('next_week')}>
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      <TimeTrackingPeriodToggle />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { title: pDaily('stats_activeHeadcount'), value: weekStats.effectif, icon: Users, color: 'from-blue-500 to-blue-600' },
          { title: pDaily('stats_present'), value: weekStats.present, icon: UserCheck, color: 'from-green-500 to-green-600' },
          { title: pDaily('stats_absent'), value: weekStats.absent, icon: UserX, color: 'from-red-500 to-red-600' },
          { title: pDaily('stats_late'), value: weekStats.late, icon: AlertTriangle, color: 'from-orange-500 to-orange-600' },
          { title: pDaily('stats_hoursWorked'), value: weekStats.totalHours, icon: Clock, color: 'from-purple-500 to-purple-600' },
        ].slice(0, 4).map((stat, i) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            className="relative group/card"
          >
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover/card:opacity-50 transition-opacity duration-500 blur-sm`} />
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 shadow-xl shadow-black/10">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm font-medium">{stat.title}</p>
                  <p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p>
                </div>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover/card:opacity-20 transition-opacity duration-300 blur-lg`} />
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}>
                    <stat.icon className="h-6 w-6 text-white force-white" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-hidden shadow-xl shadow-black/10">
        
        <div className="overflow-x-auto table-horizontal-scroll p-2">
          <table className="w-full clients-table data-table pointage-calendar-table">
            <thead className="glass-table-head">
              <tr>
                <th className="px-3 py-4 text-left pointage-sticky-col">{p('table_employee')}</th>
                {weekDates.map((date) => {
                  const isToday = toDateStr(date) === toDateStr(new Date());
                  const isWeekend = isSaudiWeekend(date);
                  return (
                    <th
                      key={toDateStr(date)}
                      className={`px-1 py-3 text-center min-w-[72px] pointage-day-head ${
                        isToday ? 'pointage-day-today' : isWeekend ? 'pointage-day-weekend' : ''
                      }`}
                    >
                      <div className={`text-lg ${isToday ? 'font-bold pointage-day-today-text' : ''}`}>{date.getDate()}</div>
                      <div className="text-xs opacity-80">{getDayOfWeek(date)}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="employees-table-body">
              {displayedEmployees.map((employee) => (
                <tr key={employee.id} className="glass-table-row">
                  <td className="px-3 py-3 pointage-sticky-col font-medium">{employee.name || p('undefinedName')}</td>
                  {(weekData[employee.id] || []).map((cell, idx) => {
                    const date = weekDates[idx];
                    const isToday = toDateStr(date) === toDateStr(new Date());
                    const isWeekend = isSaudiWeekend(date);
                    return (
                      <td
                        key={`${employee.id}-${idx}`}
                        className={`px-1 py-2 text-center cursor-pointer pointage-day-cell ${
                          isToday ? 'pointage-day-today-row' : isWeekend ? 'pointage-day-weekend-row' : ''
                        }`}
                        onClick={() => handleCellClick(employee, idx)}
                        title={p('cellTitle', {
                          label: cell.label,
                          date: formatPointageDateWithHijri(date),
                          notes: cell.notes ? ` — ${cell.notes}` : '',
                        })}
                      >
                        <div className={`pointage-code-badge inline-flex items-center justify-center w-10 h-10 rounded-full ${cell.color} text-white text-xs font-semibold`}>
                          {cell.code || '·'}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          {displayedEmployees.length === 0 && (
            <p className="text-center py-12 pointage-empty-msg">{p('empty_noEmployees')}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default WeeklyTimeTrackingPage;
